import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supplierService } from '../lib/services/supplierService';
import { Supplier, CreateSupplierFormData } from '../types/supplier';
import toast from 'react-hot-toast';
import { collection, query, orderBy, getDocs, getDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useLanguage } from '../contexts/LanguageContext';

export const useSuppliers = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const { user } = useAuth();
  const { t } = useLanguage();

  const loadSuppliers = useCallback(async () => {
    try {
      const q = query(collection(db, 'suppliers'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const loadedSuppliers = querySnapshot.docs.map(doc => ({
        ...doc.data(),
        id: doc.id,
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      })) as Supplier[];

      setSuppliers(loadedSuppliers);
    } catch (err) {
      setError(err as Error);
      toast.error('Hiba a beszállítók betöltése során');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSuppliers();
  }, [loadSuppliers]);

  const createSupplier = async (data: CreateSupplierFormData) => {
    if (!user) return;
    try {
      const id = await supplierService.create(data, user.uid);
      await loadSuppliers();
      return id;
    } catch (error) {
      console.error('Error creating supplier:', error);
      throw error;
    }
  };

  const updateSupplier = async (id: string, data: Partial<Supplier>) => {
    if (!user) return;
    try {
      await supplierService.update(id, data);
      await loadSuppliers();
    } catch (error) {
      console.error('Error updating supplier:', error);
      throw error;
    }
  };

  const deleteSupplier = async (id: string) => {
    if (!user) return;
    try {
      await supplierService.delete(id);
      setSuppliers(suppliers.filter(supplier => supplier.id !== id));
    } catch (error) {
      console.error('Error deleting supplier:', error);
      throw error;
    }
  };

  const getSupplierById = useCallback(async (id: string) => {
    try {
      const supplierDoc = await getDoc(doc(db, 'suppliers', id));
      if (supplierDoc.exists()) {
        return { ...supplierDoc.data(), id: supplierDoc.id } as Supplier;
      }
      return null;
    } catch (error) {
      console.error('Hiba a beszállító lekérdezése során:', error);
      return null;
    }
  }, []);

  const refreshSupplier = useCallback(async (id: string) => {
    try {
      const refreshedSupplier = await getSupplierById(id);
      
      if (refreshedSupplier) {
        setSuppliers(currentSuppliers => 
          currentSuppliers.map(supplier => 
            supplier.id === id ? refreshedSupplier : supplier
          )
        );
        return refreshedSupplier;
      }
      return null;
    } catch (error) {
      console.error('Error refreshing supplier:', error);
      throw error;
    }
  }, [getSupplierById]);

  return {
    suppliers,
    loading,
    error,
    createSupplier,
    updateSupplier,
    deleteSupplier,
    refresh: loadSuppliers,
    getSupplierById,
    refreshSupplier
  };
}; 